import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '@/lib/api';
import { cacheScreensaverMedia } from '@/lib/screensaverMediaCache';

interface ScreensaverMedia {
  id: string;
  image_url: string;
  display_order: number;
  media_type: 'image' | 'video';
}

interface ScreensaverProps {
  municipalityId: string;
  isActive: boolean;
  onInteraction: () => void;
}

const VIDEO_SAFETY_TIMEOUT = 120000; // 2 minutos máximo por video

export const Screensaver = ({ municipalityId, isActive, onInteraction }: ScreensaverProps) => {
  const [mediaItems, setMediaItems] = useState<ScreensaverMedia[]>([]);
  const [mediaPreloaded, setMediaPreloaded] = useState(false);
  const [delay, setDelay] = useState(5000);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar delay del municipio
        const municipalityData = await api.municipalities.get(municipalityId).catch(() => null);
        if (municipalityData?.screensaver_delay ?? municipalityData?.screensaverDelay) {
          setDelay(municipalityData.screensaver_delay ?? municipalityData.screensaverDelay);
        }

        // Cargar medios del municipio
        const municipalityMedia = await api.screensaverImages.list(municipalityId);

        // Cargar medios globales
        const globalMedia = await api.globalScreensaverImages.list();
        
        // Combinar medios: primero municipio, luego globales
        const municipalityList: ScreensaverMedia[] = (municipalityMedia || []).map(item => ({
          id: item.id,
          image_url: item.image_url,
          display_order: item.display_order,
          media_type: (item.media_type as 'image' | 'video') || 'image'
        }));

        const globalList: ScreensaverMedia[] = (globalMedia || []).map(item => ({
          id: `global-${item.id}`,
          image_url: item.image_url,
          display_order: 1000 + item.display_order, // Los globales van después
          media_type: (item.media_type as 'image' | 'video') || 'image'
        }));

        const combinedMedia = [...municipalityList, ...globalList];
        
        setMediaItems(combinedMedia);

        // Persistir los medios en Cache Storage; el service worker también
        // resuelve las peticiones Range necesarias para reproducir vídeo.
        void cacheScreensaverMedia(combinedMedia.map(media => media.image_url)).then(result => {
          if (result.failed.length > 0) {
            console.warn('No se pudieron cachear todos los medios del salvapantallas', result.failed);
          } else {
            console.log(`✅ ${result.cached} medios del salvapantallas disponibles en caché`);
          }
        });
        
        // Precargar imágenes y metadata de videos
        if (combinedMedia.length > 0) {
          setMediaPreloaded(false);
          
          const preloadPromises = combinedMedia.map((media) => {
            return new Promise<void>((resolve) => {
              if (media.media_type === 'video') {
                // Para videos, solo cargar metadata
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = () => resolve();
                video.onerror = () => resolve(); // Continuar aunque falle
                video.src = media.image_url;
              } else {
                // Para imágenes, precargar completas
                const image = new Image();
                image.onload = () => resolve();
                image.onerror = () => resolve();
                image.src = media.image_url;
              }
            });
          });
          
          try {
            await Promise.all(preloadPromises);
            console.log('✅ Medios del salvapantallas precargados (municipio + globales)');
            setMediaPreloaded(true);
          } catch (error) {
            console.error('❌ Error precargando medios:', error);
            setMediaPreloaded(true);
          }
        } else {
          setMediaPreloaded(true);
        }
      } catch (error) {
        console.error('Error loading screensaver data:', error);
        setMediaPreloaded(true);
      }
    };

    if (municipalityId) {
      loadData();
    }

    // Reload on focus instead of realtime subscription
    const handleFocus = () => { if (municipalityId) loadData(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [municipalityId]);

  // Función para avanzar al siguiente slide
  const goToNextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % mediaItems.length);
  }, [mediaItems.length]);

  // Control de transiciones manual
  useEffect(() => {
    if (!isActive || mediaItems.length === 0) {
      // Limpiar timeouts cuando no está activo
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      return;
    }

    const currentMedia = mediaItems[currentIndex];
    
    if (currentMedia.media_type === 'image') {
      // Para imágenes, usar el delay configurado
      transitionTimeoutRef.current = setTimeout(() => {
        goToNextSlide();
      }, delay);
    } else {
      // Para videos, configurar timeout de seguridad
      // El avance real se hace en onEnded del video
      safetyTimeoutRef.current = setTimeout(() => {
        console.log('⚠️ Video timeout de seguridad - avanzando');
        goToNextSlide();
      }, VIDEO_SAFETY_TIMEOUT);
    }

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, [isActive, currentIndex, mediaItems, delay, goToNextSlide]);

  // Control de videos: play/pause según visibilidad
  useEffect(() => {
    if (!isActive) {
      // Pausar todos los videos cuando el salvapantallas no está activo
      videoRefs.current.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
      setCurrentIndex(0);
      return;
    }

    // Cuando está activo, manejar el video actual
    const currentMedia = mediaItems[currentIndex];
    if (currentMedia?.media_type === 'video') {
      const videoEl = videoRefs.current.get(currentMedia.id);
      if (videoEl) {
        videoEl.currentTime = 0;
        videoEl.play().catch(err => {
          console.error('Error reproduciendo video:', err);
          // Si falla la reproducción, avanzar al siguiente
          goToNextSlide();
        });
      }
    }

    // Pausar videos que no son el actual
    videoRefs.current.forEach((video, id) => {
      if (id !== currentMedia?.id) {
        video.pause();
      }
    });
  }, [isActive, currentIndex, mediaItems, goToNextSlide]);

  const handleVideoEnded = useCallback(() => {
    // Limpiar timeout de seguridad
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    goToNextSlide();
  }, [goToNextSlide]);

  const handleVideoError = useCallback(() => {
    console.error('Error en video - avanzando al siguiente');
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    goToNextSlide();
  }, [goToNextSlide]);

  const registerVideoRef = useCallback((id: string, el: HTMLVideoElement | null) => {
    if (el) {
      videoRefs.current.set(id, el);
    } else {
      videoRefs.current.delete(id);
    }
  }, []);

  const handleInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onInteraction();
  };

  // Si aún no ha precargado, no mostrar nada
  if (!mediaPreloaded) {
    return null;
  }

  // Si no hay medios pero está activo, mostrar pantalla negra interactiva
  if (mediaItems.length === 0) {
    if (!isActive) return null;

    return (
      <div 
        className="screensaver-overlay screensaver-active"
        onClick={handleInteraction}
        onTouchStart={handleInteraction}
      >
        <div className="screensaver-slide screensaver-slide-active bg-black" />
      </div>
    );
  }

  return (
    <div 
      className={`screensaver-overlay ${isActive ? 'screensaver-active' : 'screensaver-hidden'}`}
      onClick={handleInteraction}
      onTouchStart={handleInteraction}
    >
      {mediaItems.map((media, index) => (
        <div 
          key={media.id}
          className={`screensaver-slide ${index === currentIndex ? 'screensaver-slide-active' : 'screensaver-slide-hidden'}`}
        >
          {media.media_type === 'video' ? (
            <video
              ref={(el) => registerVideoRef(media.id, el)}
              src={media.image_url}
              className="screensaver-video"
              muted
              playsInline
              crossOrigin="anonymous"
              preload={isActive && index === currentIndex ? 'auto' : 'metadata'}
              onEnded={handleVideoEnded}
              onError={handleVideoError}
            />
          ) : (
            <img
              src={media.image_url}
              alt="Imagen del salvapantallas"
              className="screensaver-image"
              loading="eager"
              decoding="async"
            />
          )}
        </div>
      ))}
    </div>
  );
};
